import {
  createPostComposerState,
  getPostComposerProviderKind,
  normalizePostComposerState,
  serializePostComposerMedia,
  serializePostComposerCTA,
  validatePostComposerStep,
  resolvePostComposerCopyCountEnabled,
} from './post-creator/postComposerModel'
import { POST_COMPOSER_SECTION_REGISTRY, sectionLabel } from './post-creator/postComposerSections'

const COMMAND_CENTER_REVIEW_SECTION = {
  id: 'review',
  labels: { commandCenter: 'Review' },
  focusId: 'post-composer-section-review',
  validate: validateCommandCenterComposerStep,
  complete: (stepIndex, currentStepIndex) => stepIndex < currentStepIndex,
  render: ({ content }) => content,
}

export const COMMAND_CENTER_SECTION_REGISTRY = [...POST_COMPOSER_SECTION_REGISTRY, COMMAND_CENTER_REVIEW_SECTION]

export const COMMAND_CENTER_COMPOSER_STEPS = COMMAND_CENTER_SECTION_REGISTRY.map((entry) => ({
  value: entry.id,
  label: sectionLabel(entry, 'commandCenter'),
}))

export function createCommandCenterComposerState(skipApproval = false) {
  return {
    ...createPostComposerState(skipApproval),
    label: '',
    tagIdsMap: {},
    targetOfferingsMap: {},
    surfaceIdsMap: {},
    bookOverrides: {},
  }
}

export function normalizeCommandCenterComposerState(savedState = {}) {
  const shared = normalizePostComposerState(savedState)
  return {
    ...createCommandCenterComposerState(Boolean(savedState.skipApproval)),
    ...shared,
    ...savedState,
    tagIdsMap: savedState.tagIdsMap || {},
    targetOfferingsMap: savedState.targetOfferingsMap || {},
    surfaceIdsMap: savedState.surfaceIdsMap || {},
    bookOverrides: savedState.bookOverrides || {},
  }
}

export function toSharedCommandCenterComposerState(formData, selectedCreator) {
  return normalizePostComposerState({
    ...formData,
    userId: selectedCreator?.id || '',
  })
}

function isoDate(value) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

function sameValue(left, right) {
  return JSON.stringify(left) === JSON.stringify(right)
}

function normalizeOverride(override) {
  if (!override || typeof override !== 'object' || !Object.hasOwn(override, 'value')) return null
  return { value: override.value }
}

const SHARED_CONTENT_OVERRIDE_FIELDS = ['title', 'subtitle', 'text', 'cta', 'media_objects']

function compareOrganizationIds(left, right) {
  if (left < right) return -1
  if (left > right) return 1
  return 0
}

function buildDestinationDistribution(formData, organizationId) {
  return {
    tag_ids: formData.tagIdsMap?.[organizationId] || [],
    surface_ids: formData.surfaceIdsMap?.[organizationId] || [],
    regulation_ids: formData.targetOfferingsMap?.[organizationId] || [],
  }
}

function buildDestinationOverrides(formData, organizationId, sharedDocument) {
  const requested = formData.bookOverrides?.[organizationId] || {}
  const overrides = {}
  for (const field of SHARED_CONTENT_OVERRIDE_FIELDS) {
    const normalized = normalizeOverride(requested[field])
    if (normalized && field === 'media_objects' && Array.isArray(normalized.value)) {
      normalized.value = serializePostComposerMedia(normalized.value)
    }
    if (normalized && !sameValue(normalized.value, sharedDocument[field])) {
      overrides[field] = normalized
    }
  }
  return overrides
}

function buildCommandCenterSharedDocumentV2(formData, selectedCreator, { isKambi, isCryptoDotCom, isOG, isEditMode } = {}) {
  const shared = toSharedCommandCenterComposerState(formData, selectedCreator)
  const providerKind = getPostComposerProviderKind({ isKambi, isCryptoDotCom, isOG })
  return {
    creator_id: shared.userId,
    label: formData.label || '',
    title: shared.title,
    subtitle: shared.subtitle,
    text: shared.text,
    media_objects: serializePostComposerMedia(shared.mediaObjects),
    cta: serializePostComposerCTA(shared, { persistDefault: isEditMode }),
    market: {
      kambi_outcome_ids: providerKind === 'kambi' ? shared.kambiOutcomeIds : [],
      provider_selection_ids: providerKind === 'cryptodotcom' || providerKind === 'og'
        ? shared.providerSelectionIds
        : [],
      wager_entries: providerKind === 'kambi' ? shared.wagerEntries : [],
    },
    advanced: {
      skip_approval: shared.skipApproval,
      post_type: shared.postType,
      expires_at: isoDate(shared.expiresAt),
      promo_priority: shared.postType === 'promotion' ? shared.promoPriority : 0,
      interleave_every: shared.postType === 'promotion' && shared.placementMode === 'interleave' && shared.interleaveEvery
        ? Number.parseInt(shared.interleaveEvery, 10)
        : null,
      scheduled_for: shared.postType === 'promotion' ? isoDate(shared.scheduledFor) : null,
      share_enabled: shared.shareEnabled !== false,
      copy_count_enabled: resolvePostComposerCopyCountEnabled(shared),
      card_presentation: shared.cardPresentation || null,
    },
  }
}

// Schema-v2 canonical document builder. Every selected organization is an equal,
// explicit destination: no source organization, no duplicate target-id list, and
// no shared distribution. Destinations are canonicalized by organization id so
// selection order never changes the semantic payload. This is the only payload
// shape the live composer serializes; schema v1 routes remain server-side only
// to satisfy already-cached frontend bundles.
export function buildCommandCenterPostGroupPayloadV2(formData, {
  selectedProviderId,
  selectedOrganizationIds = [],
  selectedCreator,
  isKambi = false,
  isCryptoDotCom = false,
  isOG = false,
  isEditMode = false,
  clientRequestId,
  expectedRevision,
} = {}) {
  const sharedDocument = buildCommandCenterSharedDocumentV2(formData, selectedCreator, {
    isKambi,
    isCryptoDotCom,
    isOG,
    isEditMode,
  })

  const destinations = [...selectedOrganizationIds]
    .sort(compareOrganizationIds)
    .map((organizationId) => ({
      organization_id: organizationId,
      distribution: buildDestinationDistribution(formData, organizationId),
      overrides: buildDestinationOverrides(formData, organizationId, sharedDocument),
    }))

  return {
    client_request_id: clientRequestId,
    provider_id: selectedProviderId,
    ...(expectedRevision == null ? {} : { expected_revision: expectedRevision }),
    document: {
      schema_version: 2,
      shared: sharedDocument,
      destinations,
    },
  }
}

export function resolveCommandCenterDestinationDocumentV2(payload, organizationId) {
  const shared = payload?.document?.shared || {}
  const destination = (payload?.document?.destinations || [])
    .find((entry) => entry.organization_id === organizationId) || {}
  const overrides = destination.overrides || {}
  const effective = {
    ...shared,
    market: { ...(shared.market || {}) },
    distribution: { ...(destination.distribution || {}) },
    advanced: { ...(shared.advanced || {}) },
  }

  for (const field of SHARED_CONTENT_OVERRIDE_FIELDS) {
    if (Object.hasOwn(overrides, field)) effective[field] = overrides[field].value
  }
  return effective
}

export function validateCommandCenterComposerStep(step, formData, {
  selectedProviderId,
  selectedOrganizationIds = [],
  selectedCreator,
  marketValidationError = '',
  reviewValidationError = '',
} = {}) {
  const sharedError = validatePostComposerStep(
    step,
    toSharedCommandCenterComposerState(formData, selectedCreator),
    {
      hasEnabledSurfaces: true,
      marketValidationError,
    },
  )
  if (sharedError) return sharedError

  if (step === 'content') {
    if (!selectedProviderId) return { step, message: 'Choose a provider before continuing.' }
    if (!selectedOrganizationIds.length) return { step, message: 'Choose at least one target organization before continuing.' }
    if (!selectedCreator?.id) return { step, message: 'Choose a creator before continuing.' }
    if (!formData.text.trim()) return { step, message: 'Add post text before continuing.' }
  }

  if (step === 'review') {
    const contentError = validateCommandCenterComposerStep('content', formData, {
      selectedProviderId,
      selectedOrganizationIds,
      selectedCreator,
    })
    if (contentError) return contentError
    const marketError = validateCommandCenterComposerStep('market', formData, {
      selectedProviderId,
      selectedOrganizationIds,
      selectedCreator,
      marketValidationError,
    })
    if (marketError) return marketError
    if (reviewValidationError) {
      return { step: 'distribution', message: reviewValidationError }
    }
  }

  return null
}
